export type HardwareVideoCodec = 'hevc' | 'h264';

export type HardwareCodecQualification = 'runtime-probe' | 'fleet-proven-h264-profile';

export type HardwareCodecFailureReason =
  | 'guest-hardware-decode-unavailable'
  | 'host-hardware-encode-unavailable'
  | 'host-hardware-encode-probe-unavailable'
  | 'no-mutual-hardware-codec'
  | 'codec-preferences-rejected'
  | 'sender-pin-rejected'
  | 'sender-pin-pending';

export const HARDWARE_VIDEO_CODEC_ORDER: readonly HardwareVideoCodec[] = ['hevc', 'h264'];
export const NO_MUTUAL_HARDWARE_VIDEO_CODEC = 'NO_MUTUAL_HARDWARE_VIDEO_CODEC';

export interface VideoCodecCapability {
  mimeType: string;
  sdpFmtpLine?: string;
  payloadType?: number;
  preferredPayloadType?: number;
}

export type HardwareCodecDecision<T> =
  | {
      kind: 'selected';
      family: HardwareVideoCodec;
      codec: T;
      hostQualification: HardwareCodecQualification;
      guestHardwareDecodeVerified: true;
    }
  | {
      kind: 'failed';
      reason: HardwareCodecFailureReason;
      consideredFamilies: readonly HardwareVideoCodec[];
    };

const AUXILIARY_SUBTYPES = new Set(['rtx', 'red', 'ulpfec', 'flexfec-03']);

export function videoCodecSubtype(mimeType: string): string {
  return mimeType.trim().toLowerCase().split('/').pop() ?? '';
}

export function hardwareVideoCodecFamily(mimeType: string): HardwareVideoCodec | null {
  const subtype = videoCodecSubtype(mimeType);
  if (subtype === 'h265' || subtype === 'hevc') return 'hevc';
  if (subtype === 'h264' || subtype === 'avc') return 'h264';
  return null;
}

function payloadTypeOf(codec: VideoCodecCapability): number | undefined {
  return codec.preferredPayloadType ?? codec.payloadType;
}

function aptOf(codec: VideoCodecCapability): number | undefined {
  const match = /(?:^|;)\s*apt=(\d+)(?:;|$)/i.exec(codec.sdpFmtpLine ?? '');
  return match ? Number(match[1]) : undefined;
}

/** One source of truth for the active HEVC/H.264 negotiation list. */
export class VideoCodecPolicy {
  public orderCapabilities<T extends VideoCodecCapability>(
    localCapabilities: readonly T[],
    remoteCapabilities?: readonly T[],
  ): T[] {
    const remoteFamilies = remoteCapabilities
      ? new Set(remoteCapabilities.map(codec => hardwareVideoCodecFamily(codec.mimeType)).filter(Boolean))
      : null;
    const primaries = localCapabilities.filter(codec => {
      const family = hardwareVideoCodecFamily(codec.mimeType);
      return family !== null && (remoteFamilies === null || remoteFamilies.has(family));
    });
    const includedPayloads = new Set(primaries.map(payloadTypeOf).filter((value): value is number => value !== undefined));
    const repairByPayload = new Map<number, T[]>();
    const sharedFec: T[] = [];

    for (const codec of localCapabilities) {
      const subtype = videoCodecSubtype(codec.mimeType);
      if (!AUXILIARY_SUBTYPES.has(subtype)) continue;
      const apt = aptOf(codec);
      if (apt === undefined) sharedFec.push(codec);
      else if (includedPayloads.has(apt)) repairByPayload.set(apt, [...(repairByPayload.get(apt) ?? []), codec]);
    }

    const ordered: T[] = [];
    for (const family of HARDWARE_VIDEO_CODEC_ORDER) {
      for (const primary of primaries.filter(codec => hardwareVideoCodecFamily(codec.mimeType) === family)) {
        ordered.push(primary);
        const payload = payloadTypeOf(primary);
        if (payload !== undefined) ordered.push(...(repairByPayload.get(payload) ?? []));
      }
    }
    ordered.push(...sharedFec);
    return ordered;
  }
}

export const videoCodecPolicy = new VideoCodecPolicy();
